import { Period } from '../types';
import { updateLoadingEffect } from './updateLoadingEffect';

/**
 * Funkce pro parsování skóre ze stringu.
 * Rozděluje hlavní a extra hodnotu skóre a vrátí je jako objekt.
 *
 * @param scoreStr - Skóre jako string.
 * @returns Objekt s hlavní a extra hodnotou skóre.
 *
 * Příklady použití:
 * const result1 = parseScore("6-7 (0)");  funkce vrací: { main: "6-7", extra: "0" }
 * const result2 = parseScore("6-7");      funkce vrací: { main: "6-7", extra: null }
 */
const parseScore = (scoreStr: string): { main: string; extra: string | null } => {
  /**
   * Regulární výraz:
   *  ^(.+?)              → zachytí čo najkratšiu časť reťazca (hlavnú hodnotu)
   *  (?:\s*\((\d+)\))?   → voliteľná časť: číslo v zátvorkách, prípadne s medzerou pred zátvorkou
   *  $                   → koniec reťazca
   */
  const regex = /^(.+?)(?:\s*\((\d+)\))?$/;

  const match = scoreStr.match(regex);
  if (match) {
    return {
      main: match[1].trim(), // Hlavná časť, napr. "6-7"
      extra: match[2] || null, // Extra hodnota, napr. "0", alebo null, ak nie je prítomná
    };
  }
  return { main: scoreStr, extra: null };
};

/**
 * Funkce pro aktualizaci hodnot setů u zápasu.
 * Aktualizuje hodnoty setů domácího i hostujícího týmu.
 *
 * @param matchElement - HTMLElement zápas, který má být aktualizován.
 * @param periods - Sety zápasu.
 * @param matchStatus - Stav zápasu.
 */
export const updateMatchSets = (
  matchElement: HTMLElement,
  periods: Period[],
  matchStatus: string
) => {
  if (!matchElement) return;

  // Filtrování setů - zachováváme pouze ty, které mají `name` obsahující "SET"
  const validSets = periods.filter((set) => set.name.includes('SET'));

  // Počet platných setů
  const setCount = validSets.length;

  const homeContainersOfSets = matchElement.querySelector(
    '[data-match-overview-set-container-score-1]'
  );
  const awayContainersOfSets = matchElement.querySelector(
    '[data-match-overview-set-container-score-2]'
  );

  if (!homeContainersOfSets || !awayContainersOfSets) {
    console.warn(
      'Elementy ([data-match-overview-set-container-score-1], [data-match-overview-set-container-score-2]) pro aktualizaci setů nebyly nalezeny.'
    );
    return null;
  }

  // Kontejnery pro domácí a hostující tým
  const homeSetContainers: NodeListOf<HTMLElement> = homeContainersOfSets.querySelectorAll(
    '[data-match-overview-set-value-1], [data-match-overview-set-value-2], [data-match-overview-set-value-3], [data-match-overview-set-value-4], [data-match-overview-set-value-5]'
  );
  const awaySetContainers: NodeListOf<HTMLElement> = awayContainersOfSets.querySelectorAll(
    '[data-match-overview-set-value-1], [data-match-overview-set-value-2], [data-match-overview-set-value-3], [data-match-overview-set-value-4], [data-match-overview-set-value-5]'
  );

  // Aktualizace hodnot setů pro domácí a hostující tým
  validSets.forEach((set, index) => {
    const rawScore = parseScore(set.value_colon);

    const [homeSetScore, awaySetScore] = rawScore.main.split(':'); // Rozdelenie skóre "2:6" -> home: 2, away: 6
    const extraScore = rawScore.extra;
    const isWinnerHomeTeam = Number(homeSetScore) > Number(awaySetScore);
    const isWinnerAwayTeam = Number(homeSetScore) < Number(awaySetScore);

    const tiebreakNumber = extraScore ? Number(extraScore) : null;
    const winnerTiebreak =
      tiebreakNumber && tiebreakNumber < 7 ? 7 : tiebreakNumber && tiebreakNumber + 2;

    // Nastavení skóre pro domácího hráče
    if (
      homeSetContainers &&
      homeSetContainers[index] &&
      homeSetContainers[index].textContent !== homeSetScore
    ) {
      homeSetContainers[index].textContent = homeSetScore || '';
      homeSetContainers[index].classList.add(`active-set`);
      updateLoadingEffect(homeSetContainers[index]);

      if (winnerTiebreak && tiebreakNumber) {
        const sup = document.createElement('sup');
        sup.textContent = isWinnerHomeTeam ? winnerTiebreak.toString() : tiebreakNumber.toString();

        homeSetContainers[index].appendChild(sup);
      }
    }

    // Nastavení skóre pro hostujícího hráče
    if (
      awaySetContainers &&
      awaySetContainers[index] &&
      awaySetContainers[index].textContent !== awaySetScore
    ) {
      awaySetContainers[index].textContent = awaySetScore || '';
      awaySetContainers[index].classList.add(`active-set`);
      updateLoadingEffect(awaySetContainers[index]);

      if (winnerTiebreak && tiebreakNumber) {
        const sup = document.createElement('sup');
        sup.textContent = isWinnerAwayTeam ? winnerTiebreak.toString() : tiebreakNumber.toString();

        awaySetContainers[index].appendChild(sup);
      }
    }
  });

  // Odstranění třídy 'active-live-set' ze všech setů, aby se zabránilo duplicitě
  homeSetContainers.forEach((container) => container.classList.remove('active-live-set'));
  awaySetContainers.forEach((container) => container.classList.remove('active-live-set'));

  // Přidání 'active-live-set' na poslední validní set
  if (setCount > 0 && matchStatus === 'live') {
    homeSetContainers[setCount - 1].classList.add('active-live-set');
    awaySetContainers[setCount - 1].classList.add('active-live-set');
  } else {
    homeSetContainers[setCount - 1].classList.remove('active-live-set');
    awaySetContainers[setCount - 1].classList.remove('active-live-set');
  }

  // Vyčištění zbývajících neaktivních setů
  for (let i = setCount; i < homeSetContainers.length; i++) {
    const homeEmpty = matchElement.querySelector(`[data-match-overview-set-value-${i + 1}]`);
    if (homeEmpty) homeEmpty.textContent = '';

    const awayEmpty = matchElement.querySelectorAll(`[data-match-overview-set-value-${i + 1}]`)[1];
    if (awayEmpty) awayEmpty.textContent = '';
  }
};
